// src/lib/jobTicket.js
// ───────────────────────────────────────────────────────────────────────────
// Job Ticket (PDF) — "hoja viajera" legible que resume TODO el trabajo y sirve para
// consulta offline + entender los archivos del backup (PLAN-BACKUP-CONTINUIDAD §4, Fase A).
// Se genera del lado cliente (jsPDF) a partir de los datos del job. NO requiere servidor.
// ───────────────────────────────────────────────────────────────────────────
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import { artifactType } from "./artifacts";

// Carga una imagen (logo / preview) como dataURL para incrustarla. Best-effort: si falla
// (CORS, sin red, sin imagen) devuelve null y el ticket se genera igual sin ella.
// REDUCE las imágenes grandes a `maxDim` px y las re-encoda como JPEG: en jobs viejos los
// previews suelen ser fotos a resolución completa (varios MB); incrustar muchas a tamaño
// real revienta la memoria del PDF (RangeError "Invalid string length"). El down-scale las
// deja livianas sin afectar la calidad visible de una miniatura.
async function loadImage(url, maxDim = 700) {
  if (!url) return null;
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    const dataUrl = await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(blob);
    });
    const baseImg = await new Promise((res) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => res(null);
      im.src = dataUrl;
    });
    if (!baseImg) return null;
    const w0 = baseImg.naturalWidth || 1, h0 = baseImg.naturalHeight || 1;
    // Imagen grande → reescala y re-encoda como JPEG (fondo blanco para transparencias).
    if (Math.max(w0, h0) > maxDim) {
      const scale = maxDim / Math.max(w0, h0);
      const cw = Math.max(1, Math.round(w0 * scale)), ch = Math.max(1, Math.round(h0 * scale));
      const canvas = document.createElement("canvas");
      canvas.width = cw; canvas.height = ch;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, cw, ch);
      ctx.drawImage(baseImg, 0, 0, cw, ch);
      return { dataUrl: canvas.toDataURL("image/jpeg", 0.75), format: "JPEG", w: cw, h: ch };
    }
    // Pequeña → tal cual, detectando el formato.
    const m = /^data:image\/(\w+);/.exec(dataUrl || "");
    let fmt = (m ? m[1] : "png").toUpperCase();
    if (fmt === "JPG") fmt = "JPEG";
    return { dataUrl, format: fmt, w: w0, h: h0 };
  } catch (_) { return null; }
}

const fmtDate = (v) => {
  try {
    if (!v) return "—";
    if (typeof v?.toDate === "function") return v.toDate().toLocaleDateString();
    if (typeof v === "string") return v;
    return new Date(v).toLocaleDateString();
  } catch (_) { return "—"; }
};

const JOB_TYPE_EN = { GangRun: "Gang Run / Combo", Servicio: "Service", Proyecto: "Project", Producto: "Product" };
const jobTypeLabel = (jobData) => JOB_TYPE_EN[jobData?.type] || (jobData?.type || "Project");

// Estado (ES en datos) → etiqueta en inglés para el ticket.
const STATUS_EN = {
  "Finalizado": "Finished", "Pendiente por Facturar": "Pending Invoice",
  "En Hold": "On Hold", "Hold": "On Hold", "Pendiente": "Pending",
  "Listo para Prepress": "Ready for Prepress", "En Ruta": "Ready for Prepress",
  "Listo para Producción": "Ready for Production",
  "Devuelto a Diseño": "Returned to Design", "Devuelto a Revisión": "Returned to Design",
  "Corregido por Diseño": "Corrected by Design", "Reproducir": "Reprint",
  "Producido": "Produced", "Impresa": "Printed", "Cortada": "Cut",
  "Impresa y Cortada": "Printed & Cut",
};
const enStatus = (s) => STATUS_EN[s] || s || "—";

const sheetName = (s, jobData) =>
  s.hri || `#${String(jobData?.id || "").replace("J-", "")}_${String(s.sheetNumber || "").padStart(3, "0")}`;

const sheetTipo = (s) => (!s.isPrinting ? "Cut only" : Number(s.printFaces) === 2 ? "2 sides" : "1 side");

/**
 * Construye y descarga el Job Ticket PDF.
 * @param {{ jobId, jobData, client, sheets, tasks, artifacts, materialsCatalog }} ctx
 */
export async function buildJobTicketPdf(ctx) {
  // En jobs muy grandes/viejos, el PDF con todas las miniaturas puede exceder el límite de
  // string de JS ("Invalid string length") al ensamblarse. Si eso pasa, se regenera SIN las
  // imágenes pesadas (plate thumbnails, previews, cover) → el usuario SIEMPRE obtiene el ticket.
  try {
    return await renderJobTicket(ctx, true);
  } catch (e) {
    if (String(e?.message || e).includes("Invalid string length")) {
      console.warn("Job Ticket: el PDF con imágenes excede el límite; regenerando sin miniaturas.", e);
      return await renderJobTicket(ctx, false);
    }
    throw e;
  }
}

async function renderJobTicket(ctx, withImages = true) {
  const { jobId, jobData = {}, client, sheets = [], tasks = [], artifacts = [], materialsCatalog = [], output = "save" } = ctx;
  const doc = new jsPDF({ unit: "pt", format: "letter" }); // 612 x 792 pt
  const PAGE_W = doc.internal.pageSize.getWidth();
  const MARGIN = 40;
  const RIGHT = PAGE_W - MARGIN;
  const matName = (s) => materialsCatalog.find((m) => m.id === s.materialId)?.name || s.materialName || "—";
  const nowStr = new Date().toLocaleString();

  // Jobs viejos cerrados sin marcar planchas → producción inferida como completa (igual que el dashboard).
  const _phase = jobData.status?.phase || jobData.status?.stage || jobData.status;
  const jobDone = _phase === "Finalizado" || _phase === "Pendiente por Facturar";
  const effPrinted = (s) => (jobDone && s.isPrinting ? (Number(s.repetitions) || 0) : (Number(s.printedCount) || 0));
  const effCut = (s) => (jobDone ? (Number(s.repetitions) || 0) : (Number(s.cutCount) || 0));

  // --- Imágenes (best-effort) --- logo y QR son livianos (siempre); el cover (preview)
  // es pesado → solo en el primer intento (withImages).
  const [logo, preview, qr] = await Promise.all([
    loadImage(`${process.env.PUBLIC_URL || ""}/cartonbar-logo.png`),
    withImages ? loadImage(jobData.coverImageUrl) : Promise.resolve(null),
    QRCode.toDataURL(JSON.stringify({ jobId: jobData.id || jobId }), { margin: 1, width: 160 }).then((d) => ({ dataUrl: d, format: "PNG" })).catch(() => null),
  ]);

  // ── Encabezado ──────────────────────────────────────────────────────────
  let y = MARGIN;
  let logoBottom = y;
  if (logo) {
    const lw = 130; // logo
    const lh = lw * ((logo.h || 1) / (logo.w || 1));
    try { doc.addImage(logo.dataUrl, logo.format, MARGIN, y, lw, lh); } catch (_) {}
    logoBottom = y + lh;
  }
  // QR arriba-derecha
  if (qr) { try { doc.addImage(qr.dataUrl, "PNG", RIGHT - 70, y - 4, 70, 70); } catch (_) {} }
  // Título centrado DEBAJO del logo y el QR, para que el wordmark no se monte sobre el texto.
  const titleY = Math.max(y + 22, logoBottom + 18);
  doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(0);
  doc.text("JOB TICKET", PAGE_W / 2, titleY, { align: "center" });
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(40);
  doc.text(`${jobData.id || jobId} · ${jobData.jobName || ""}`, PAGE_W / 2, titleY + 18, { align: "center" });
  y = Math.max(logoBottom, titleY + 18) + 10;
  doc.setDrawColor(220); doc.line(MARGIN, y, RIGHT, y); y += 8;

  // ── Identificación (key/value) — incluye detalles (PO / Sales order / descripción) ──
  const idRows = [
    ["Client", client?.name || jobData.clientName || "—"],
    ["Job type", jobTypeLabel(jobData)],
    ["Status", enStatus(jobData.status?.phase || jobData.status?.stage || jobData.status)],
    ["Created", fmtDate(jobData.createdAt)],
    ["Committed delivery", fmtDate(jobData.deliveryDate)],
    ["Sales order (ERP)", jobData.salesOrder || "—"],
    ["Purchase order (PO)", jobData.purchaseOrder || "—"],
    ["Main material", jobData.materialName || (materialsCatalog.find((m) => m.id === jobData.materialId)?.name) || "—"],
    ["Sheet", jobData.sheet?.width ? `${jobData.sheet.width} × ${jobData.sheet.height} ${jobData.sheet.unit || "in"}` : "—"],
  ];
  if ((jobData.description || "").trim()) idRows.push(["Description", jobData.description.trim()]);
  // La imagen del trabajo va a la derecha; la tabla deja ese ancho libre (IMG_W).
  const IMG_W = preview ? 160 : 0;
  const idStartY = y;
  autoTable(doc, {
    startY: y, margin: { left: MARGIN, right: MARGIN + IMG_W + (IMG_W ? 12 : 0) },
    theme: "plain", styles: { fontSize: 9, cellPadding: 2, overflow: "linebreak" },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 130, textColor: [90, 90, 90] } },
    body: idRows,
  });
  const idEndY = doc.lastAutoTable.finalY;
  // Imagen del trabajo, agrandada para llenar la altura del bloque de información.
  if (preview) {
    const boxH = Math.max(64, idEndY - idStartY);
    const ratio = (preview.w || 1) / (preview.h || 1);
    let w = IMG_W, h = w / ratio;
    if (h > boxH) { h = boxH; w = h * ratio; }
    const x = RIGHT - IMG_W + (IMG_W - w) / 2;
    const iy = idStartY + (boxH - h) / 2;
    try { doc.addImage(preview.dataUrl, preview.format, x, iy, w, h); } catch (_) {}
  }
  y = idEndY + 6;

  const sectionTitle = (txt) => {
    if (y > 720) { doc.addPage(); y = MARGIN; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(30);
    doc.text(txt, MARGIN, y); y += 4;
    doc.setDrawColor(230); doc.line(MARGIN, y, RIGHT, y); y += 6;
  };

  // Cada sección va aislada: si un job VIEJO trae un registro con forma inesperada,
  // se omite ESA sección (queda en consola) y el resto del ticket se genera igual,
  // en vez de tumbar todo el PDF.
  const section = (label, fn) => { try { fn(); } catch (e) { console.warn(`Job Ticket: sección "${label}" omitida:`, e); } };

  // ── Resumen de materiales para FACTURACIÓN (desglose por material) ────────────
  // Por material: total de planchas, 1 cara / 2 caras / solo corte (tipo de plancha,
  // particionan el total) e Impresas / Cortadas (cantidades producidas).
  section("Materials", () => {
    const M = {};
    const T = { reps: 0, c1: 0, c2: 0, cut0: 0, printed: 0, cut: 0 };
    sheets.forEach((s) => {
      const reps = Number(s.repetitions) || 0;
      const n = matName(s);
      if (!M[n]) M[n] = { reps: 0, c1: 0, c2: 0, cut0: 0, printed: 0, cut: 0 };
      const printing = !!s.isPrinting;
      const twoSide = Number(s.printFaces) === 2;
      M[n].reps += reps;
      if (printing && !twoSide) M[n].c1 += reps;
      if (printing && twoSide) M[n].c2 += reps;
      if (!printing) M[n].cut0 += reps;
      M[n].printed += effPrinted(s);
      M[n].cut += effCut(s);
    });
    Object.values(M).forEach((v) => { T.reps += v.reps; T.c1 += v.c1; T.c2 += v.c2; T.cut0 += v.cut0; T.printed += v.printed; T.cut += v.cut; });
    const rows = Object.entries(M)
      .sort((a, b) => b[1].reps - a[1].reps)
      .map(([n, v]) => [n, String(v.reps), String(v.c1), String(v.c2), String(v.cut0), String(v.printed), String(v.cut), T.reps > 0 ? `${Math.round((v.reps / T.reps) * 100)}%` : "—"]);
    if (rows.length) {
      sectionTitle("Materials (for invoicing — plates per material)");
      autoTable(doc, {
        startY: y, margin: { left: MARGIN, right: MARGIN },
        styles: { fontSize: 7.5, cellPadding: 2.5, halign: "center", valign: "middle" },
        headStyles: { fillColor: [55, 65, 81], halign: "center" },
        columnStyles: { 0: { cellWidth: 120, halign: "left" } },
        head: [["Material", "Plates", "1-Side", "2-Side", "Cut-Only", "Printed", "Cut", "%"]],
        body: rows,
        foot: [["Total", String(T.reps), String(T.c1), String(T.c2), String(T.cut0), String(T.printed), String(T.cut), "100%"]],
        footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: "bold", halign: "center" },
      });
      y = doc.lastAutoTable.finalY + 8;
    }
  });

  // ── Plates / production (con miniatura por fila) ──────────────────────────
  if (sheets.length) {
    // Precarga la miniatura (preview) de cada plancha; loadImage nunca lanza (best-effort).
    // En el reintento sin imágenes (withImages=false) se omiten para acotar el tamaño del PDF.
    const plateImgs = withImages ? await Promise.all(
      sheets.map((s) => (s.previewImageFrontUrl ? loadImage(s.previewImageFrontUrl) : Promise.resolve(null)))
    ) : [];
    section("Plates / Production", () => {
      sectionTitle("Plates / Production");
      autoTable(doc, {
        startY: y, margin: { left: MARGIN, right: MARGIN },
        styles: { fontSize: 8, cellPadding: 3, valign: "middle" },
        headStyles: { fillColor: [55, 65, 81] },
        columnStyles: { 0: { cellWidth: 50, minCellHeight: 38 } },
        head: [["Preview", "Plate", "Material", "Type", "Reps", "Status", "Print", "Cut"]],
        body: sheets.map((s) => [
          "", sheetName(s, jobData), matName(s), sheetTipo(s),
          String(s.repetitions || 0), enStatus(s.status),
          `${effPrinted(s)}/${s.repetitions || 0}`, `${effCut(s)}/${s.repetitions || 0}`,
        ]),
        didParseCell: (data) => { if ([4, 6, 7].includes(data.column.index)) data.cell.styles.halign = "center"; },
        didDrawCell: (data) => {
          if (data.section !== "body" || data.column.index !== 0) return;
          const im = plateImgs[data.row.index];
          if (!im) return;
          const pad = 2, maxW = data.cell.width - pad * 2, maxH = data.cell.height - pad * 2;
          const ratio = (im.w || 1) / (im.h || 1);
          let w = maxW, h = w / ratio;
          if (h > maxH) { h = maxH; w = h * ratio; }
          try { doc.addImage(im.dataUrl, im.format, data.cell.x + (data.cell.width - w) / 2, data.cell.y + (data.cell.height - h) / 2, w, h); } catch (_) {}
        },
      });
      y = doc.lastAutoTable.finalY + 8;
    });
  }

  // ── Líneas del Gang (si aplica) ──────────────────────────────────────────
  if (Array.isArray(jobData.gangLines) && jobData.gangLines.length) {
    section("Gang Lines", () => {
      sectionTitle("Gang Lines (Client + SKU)");
      autoTable(doc, {
        startY: y, margin: { left: MARGIN, right: MARGIN },
        styles: { fontSize: 8, cellPadding: 3 }, headStyles: { fillColor: [55, 65, 81] },
        head: [["Client", "SKU", "Product", "Ordered", "Ups"]],
        body: jobData.gangLines.map((l) => [l.clientName || "—", l.sku || "—", l.pieceName || "—", String(l.orderedQty || 0), String(l.ups || 1)]),
        didParseCell: (data) => { if (data.column.index === 3 || data.column.index === 4) data.cell.styles.halign = "center"; },
      });
      y = doc.lastAutoTable.finalY + 8;
    });
  }

  // ── Piezas (agregadas desde las planchas) ─────────────────────────────────
  // Agregado por código, compartido por la tabla Pieces y por las References.
  // `plates` = en qué plancha(s) aparece la pieza; `total` = cantidad total en el job.
  const pieceAgg = {};
  sheets.forEach((s) => {
    const reps = Number(s.repetitions) || 0;
    const pn = sheetName(s, jobData);
    (s.pieces || []).forEach((p) => {
      if (!p.code) return;
      const k = p.code;
      if (!pieceAgg[k]) pieceAgg[k] = { code: p.code, name: p.name || "", perSheet: 0, total: 0, plates: new Set(), previewUrl: "" };
      pieceAgg[k].perSheet += Number(p.qtyPerSheet) || 0;
      pieceAgg[k].total += (Number(p.qtyPerSheet) || 0) * reps;
      pieceAgg[k].plates.add(pn);
      if (!pieceAgg[k].previewUrl && p.previewUrl) pieceAgg[k].previewUrl = p.previewUrl;
    });
  });
  const pieceList = Object.values(pieceAgg);
  // Miniatura por pieza (gated: en el reintento sin imágenes se omiten para acotar el tamaño).
  const pieceImgs = withImages ? await Promise.all(
    pieceList.map((p) => (p.previewUrl ? loadImage(p.previewUrl) : Promise.resolve(null)))
  ) : [];

  section("Pieces", () => {
    if (pieceList.length) {
      sectionTitle("Pieces");
      autoTable(doc, {
        startY: y, margin: { left: MARGIN, right: MARGIN },
        styles: { fontSize: 8, cellPadding: 3, valign: "middle" }, headStyles: { fillColor: [55, 65, 81] },
        columnStyles: { 0: { cellWidth: 46, minCellHeight: 34 }, 3: { overflow: "linebreak" } },
        head: [["Preview", "Code", "Name", "Plate(s)", "Per Sheet", "Total"]],
        body: pieceList.map((p) => ["", p.code, p.name, [...p.plates].join(", "), String(p.perSheet), String(p.total)]),
        didParseCell: (data) => { if (data.column.index === 4 || data.column.index === 5) data.cell.styles.halign = "center"; },
        didDrawCell: (data) => {
          if (data.section !== "body" || data.column.index !== 0) return;
          const im = pieceImgs[data.row.index];
          if (!im) return;
          const pad = 2, maxW = data.cell.width - pad * 2, maxH = data.cell.height - pad * 2;
          const ratio = (im.w || 1) / (im.h || 1);
          let w = maxW, h = w / ratio;
          if (h > maxH) { h = maxH; w = h * ratio; }
          try { doc.addImage(im.dataUrl, im.format, data.cell.x + (data.cell.width - w) / 2, data.cell.y + (data.cell.height - h) / 2, w, h); } catch (_) {}
        },
      });
      y = doc.lastAutoTable.finalY + 8;
    }
  });

  // (Sección "References (previews)" eliminada: las miniaturas ya van en las tablas
  //  de Plates y Pieces, así que la galería era redundante.)

  // ── Índice de archivos (puente al backup) ────────────────────────────────
  section("File Index", () => {
    sectionTitle("File Index (backup)");
    if (artifacts.length) {
      autoTable(doc, {
        startY: y, margin: { left: MARGIN, right: MARGIN },
        styles: { fontSize: 7.5, cellPadding: 3, overflow: "linebreak" }, headStyles: { fillColor: [55, 65, 81] },
        head: [["Type", "Standard name", "v", "Approval", "Original", "Backup path"]],
        columnStyles: { 5: { textColor: [90, 90, 90] } },
        didParseCell: (data) => { if (data.column.index === 2) data.cell.styles.halign = "center"; },
        body: artifacts.map((a) => [
          artifactType(a.type)?.label || a.type || "—",
          a.latestName || "—",
          String(a.currentVersion || 0),
          a.approval?.status || "pending",
          a.latestOriginalName || "—",
          a.latestName ? `jobs/${jobData.id || jobId}/artifacts/${a.latestName}` : "—",
        ]),
      });
      y = doc.lastAutoTable.finalY + 8;
    } else {
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(120);
      doc.text("No versioned files for this job.", MARGIN, y + 4); y += 16;
    }
    // Documentos subidos al job (manual / presentación / render / otros).
    const docs = (jobData.documents || []).filter(Boolean);
    if (docs.length) {
      autoTable(doc, {
        startY: y, margin: { left: MARGIN, right: MARGIN },
        styles: { fontSize: 7.5, cellPadding: 3, overflow: "linebreak" }, headStyles: { fillColor: [55, 65, 81] },
        head: [["Document", "Category", "Description", "Uploaded by"]],
        body: docs.map((d) => [d.name || "—", d.category || "—", d.description || "", d.uploadedByName || "—"]),
      });
      y = doc.lastAutoTable.finalY + 8;
    }
  });

  // ── Notes / instructions ──────────────────────────────────────────────────
  section("Notes", () => {
    const notas = (jobData.notes || jobData.description || "").trim();
    if (notas) {
      sectionTitle("Notes / Instructions");
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(40);
      const lines = doc.splitTextToSize(notas, RIGHT - MARGIN);
      if (y + lines.length * 11 > 740) { doc.addPage(); y = MARGIN; }
      doc.text(lines, MARGIN, y + 4); y += lines.length * 11 + 8;
    }
  });

  // ── Tareas / checklist ───────────────────────────────────────────────────
  if (tasks.length) {
    section("Tasks", () => {
      sectionTitle("Tasks / Checklist");
      autoTable(doc, {
        startY: y, margin: { left: MARGIN, right: MARGIN },
        styles: { fontSize: 8, cellPadding: 3 }, headStyles: { fillColor: [55, 65, 81] },
        head: [["Status", "Task", "Assigned to"]],
        body: tasks.map((t) => [t.status === "completed" ? "[X] Done" : "[ ] Pending", t.title || t.description || "—", t.assignedToName || "—"]),
      });
      y = doc.lastAutoTable.finalY + 8;
    });
  }

  // ── Firmas por etapa ──────────────────────────────────────────────────────
  section("Traceability", () => {
    if (y > 690) { doc.addPage(); y = MARGIN; }
    sectionTitle("Traceability (initials per stage)");
    const etapas = ["Design", "Prepress", "Printing", "Cutting", "Finishing", "QC", "Shipping"];
    const colW = (RIGHT - MARGIN) / etapas.length;
    doc.setFontSize(7); doc.setTextColor(80);
    etapas.forEach((e, i) => {
      const x = MARGIN + i * colW;
      doc.setDrawColor(180); doc.rect(x + 2, y, colW - 4, 34);
      doc.text(e, x + 5, y + 10);
    });
    y += 44;
  });

  // ── Pie: sello de respaldo + paginación ───────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7); doc.setTextColor(130);
    doc.text(`Backup copy · data as of ${nowStr} · CARTONBAR`, MARGIN, 782);
    doc.text(`Page ${i}/${pages}`, RIGHT - 40, 782);
  }

  if (output === "blob") return doc.output("blob");
  doc.save(`JOB-TICKET_${jobData.id || jobId}.pdf`);
}
